import classNames from "classnames"
import { useEffect, useRef, useState } from "react"
import { filter, flexAlign, flexJustify, opacity } from "./functions"
import useFontLoadDetector from "../../src/useFontLoadDetector"


// Used by the auto-sizer when auto-scaling the font size in text boxes
const minFontSize = 10  // pixels
const maxFontSize = 400 // pixels

// This was the median ratio based on our initial set of fonts
const defaultLineHeightRatio = 1.2


export default function TextboxLayer({layer, editingText, changeLayers, readOnly, onBlur}) {

  const resizedAfterMount = useRef(false)
  const oldLayerData = useRef(layer)
  const autoSizeTextRef = useRef(null)
  const [fontIsReady, setFontIsReady] = useState(false)

  const [textAreaHeight, setTextAreaHeight] = useState(layer.height)

  const {testElements} = useFontLoadDetector({
    fontFamily: layer.metadata.font,
    onLoad: () => setFontIsReady(true),
    onError: () => setFontIsReady(true) // Proceed even if font fails to load
  })


  useEffect(() => {
    if (readOnly) return

    const fontResizeNeeded = (
      ( layer.width            !== oldLayerData.current.width            && !isNaN(layer.width))  ||
      ( layer.height           !== oldLayerData.current.height           && !isNaN(layer.height)) ||
      ( layer.metadata.spacing !== oldLayerData.current.metadata.spacing && !isNaN(layer.metadata.spacing)) ||
      ( layer.metadata.kerning !== oldLayerData.current.metadata.kerning && !isNaN(layer.metadata.kerning)) ||
      ( layer.metadata.text    !== oldLayerData.current.metadata.text)   ||
      ( layer.metadata.font    !== oldLayerData.current.metadata.font)   ||
      ( layer.metadata.text    !== oldLayerData.current.metadata.text)   ||
      ( !resizedAfterMount.current )
    )

    oldLayerData.current = layer

    // If we aren't resizing the font, just set the height of the text area
    if (!fontResizeNeeded) {
      if (autoSizeTextRef.current) {
        setTextAreaHeight(autoSizeTextRef.current.scrollHeight)
      }
      return
    }

    // Wait for font to be ready before resizing
    if (!fontIsReady) return

    const element = autoSizeTextRef.current
    const {size, spacing, height} = getBestSizing(layer, element)

    if (!size) {
      console.log('Failed to auto-size text layer', layer.id)
      return
    }

    // Avoid another resize after calling `changeLayers`
    oldLayerData.current.metadata.size = size
    oldLayerData.current.metadata.spacing = spacing

    changeLayers(
      [layer.id],
      {size, spacing},
      {historySquashKey: `text:${layer.id}`},
    )
    setTextAreaHeight(height)

    resizedAfterMount.current = true

  }, [layer, readOnly, fontIsReady])


  const fontStyle = {
    fontStyle:      layer.metadata.text ? 'normal' : 'italic',
    fontSize:       layer.metadata.text ? fontSize(layer) : 100,
    fontFamily:     layer.metadata.font + ',Arial',
    filter:         filter(layer),
    textAlign:      layer.metadata.alignX,
    lineHeight:     lineHeight(layer) + 'px',
    marginTop:      verticalMargin(layer),
    marginBottom:   verticalMargin(layer),
    paddingTop:     verticalMargin(layer) * 2,
    paddingBottom:  verticalMargin(layer) * 2,
    letterSpacing:  letterSpacing(layer),
    color:          layer.metadata.text ? layer.color ?? '#111111B3' : '#666666B3',
    opacity:        opacity(layer),
    wordWrap:       'break-word',
    whiteSpace:     'pre-wrap',
    border:         'none',
    visibility:      fontIsReady ? 'visible' : 'hidden',
    overflowWrap:   'normal' // Prevents long words from breaking the layout
  }

  const textareaStyle = {
    ...fontStyle,
    borderWidth:  0,
    color:        layer.color ?? '#111111B3',
    outline:      'none',
    overflowY:    'hidden',
    overflowX:    'hidden',
    resize:       'none',
    background:   'transparent',
    width:         layer.width - 2,
  }


  return <>
    {testElements}
    <div
      className='absolute-full flex-row'
      style={{
        alignItems:     flexAlign(layer),
        justifyContent: flexJustify(layer),
        overflow: 'hidden',
      }}
    >
      { readOnly ?
        <div className="absolute-full" style={fontStyle}>
          {'\u200b' + (layer.metadata.text || 'Double click to add text') + '\u200b'}
        </div>
      :
        <div className="relative" style={{

        }}>
          <textarea
            id={"text-layer-sizer-" + layer.id}
            ref={autoSizeTextRef}
            style={{...textareaStyle, height: 1}}
            value={layer.metadata.text}
            className='absolute NOT_invisible pointer-events-none'
            readOnly
          />

          <textarea
            id={"text-layer-editor-" + layer.id}
            style={{...textareaStyle, height: textAreaHeight}}
            value={layer.metadata.text || (editingText ? '' : 'Tap to Edit')}
            onChange={e => {changeLayers([layer.id], {text: e.target.value})}}
            onBlur={onBlur}
            className={classNames('', {'pointer-events-none': !editingText} )}
          />
        </div>
      }
    </div>
  </>
}


// In the browser, we simulate line spacing by adjusting the line height, but
// that is not quite the same thing, because it also affects the vertical
// position of the text (when the alignment is not verically centered). So, we
// need to adjust the margins to correct for that.
function verticalMargin(layer) {
  return (fontSize(layer) - lineHeight(layer)) / 2.75
}


function letterSpacing (layer) {
  let {kerning} = layer.metadata
  kerning ||= 0
  return Number(kerning)
}


function fontSize (layer) {
  let {size} = layer.metadata
  size ||= 12
  return Number(size)
}


function lineSpacing (layer) {
  let {spacing} = layer.metadata
  spacing ||= 0
  return Number(spacing)
}


// Each font has a different ratio between font size and the line height, and
// for some fonts, it line height also varies (a little) at different sizes. We
// pre-calculate the ratio based on the line height at the 100px font size.
function lineHeight (layer) {
  const ratio = layer.font?.lineHeightRatio || defaultLineHeightRatio
  return fontSize(layer) * ratio + lineSpacing(layer)
}


function getBestSizing(layer, element) {

  if (!element) {
    console.log('Cannot find sizer element for layer', layer.id)
    return
  }

  let min = minFontSize
  let max = maxFontSize
  let steps = 0
  const maxSteps = 100

  while (min < max && steps < maxSteps) {
    steps += 1

    // ---------------------------------------------------------------------
    // Note that we use the `Math.ceil` function, which means that when the
    // min and max are one pixel apart, we will be testing the max value. If
    // the resulting text is too big for the layer, we will set the max
    // value to be one pixel smaller than the current value. So, we can be
    // sure that we will eventually converge on the correct value. If we
    // used `floor` instead, we would be testing the min value, and we could
    // get stuck in an infinite loop where we keep testing the min value.
    // ---------------------------------------------------------------------

    // Split the distance between the min and max font size.
    let newSize = Math.ceil((min + max) / 2)

    applySizeToElement({layer, size: newSize, element})

    // Compare the size of the element to the desired height of the layer
    const targetHeight  = layer.height
    const targetWidth   = layer.width + 10
    const currentHeight = element.scrollHeight
    const currentWidth  = element.scrollWidth

    if (currentHeight > targetHeight || currentWidth > targetWidth) {
      // The text is too big, so the size must be at least on pixel lower
      // console.log('too big', 'lowering max to', newSize - 1)
      max = newSize - 1
    } else {
      // The text is too small, so we need to make the font bigger
      // console.log('too small', 'raising min to', newSize)
      min = newSize
    }

    // Check if we have found the final size, so we can stop resizing
    if (min >= max) {
      // NOTE: Need to use `max` here, because `newSize` may have been too
      // big and the `max` is one pixel smaller than the `min` value.
      const finalSize = max

      const {metadata} = applySizeToElement({layer, size: finalSize, element})
      const finalSpacing = metadata.spacing
      const finalHeight = element.scrollHeight

      // console.log('found final size', finalSize, 'for layer', layer.id, 'after', steps, 'steps')

      return {
        size:    finalSize,
        spacing: finalSpacing,
        height:  finalHeight,
      }
    }
  }

  console.log('🔥 Failed to find a font size for layer', layer.id, 'after', steps, 'steps')

  return {}
}


function applySizeToElement ({layer, size, element}) {
  // Also adjust the line spacing to keep it proportional to the font size
  const spacingRatio = layer.metadata.size ? layer.metadata.spacing / layer.metadata.size : 0
  const newSpacing = Math.round(size * spacingRatio)

  const adjustedLayerData = {
    ...layer,
    metadata: {
      ...layer.metadata,
      size:    size,
      spacing: newSpacing,
    },
  }

  // Apply the same styling rules used the element during render
  const style = {
    fontSize:       fontSize(adjustedLayerData) + 'px',
    lineHeight:     lineHeight(adjustedLayerData) + 'px',
    paddingTop:     verticalMargin(layer) * 2,
    paddingBottom:  verticalMargin(layer) * 2,
  }

  Object.assign(element.style, style)

  return adjustedLayerData
}

//----------------------------------------------------------------------------
// NOTE: Chrome allows bolding of fonts that do not have a bold variant. This
// "faux bold" is basically a thicker text-stroke, but the thinkness of the
// stroke varies based on the font size. Also, on smaller fonts, the "faux bold"
// effect won't kick in until the font-weight is higher (e.g. 900/bolder).
// Rather than use that, we use the different fonts for the different weights.
//----------------------------------------------------------------------------
